import { FC, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  toggleLoader,
  toggleToast,
} from "../../../redux/slices/appState/appStateSlice";
import { MuiTypography, MuiModal } from "../..";
import { useDeleteClientSavedFiltersMutation } from "../../../api/services/client/clientApis";
import {
  Checkbox,
  List,
  ListItem,
  ListItemText,
  Container,
} from "@mui/material";
import { MuiBox, MuiGrid } from "../../index";
import { DeleteMultipleFiltersModalWrapper } from "./DeleteMultipleFilters.style";
import { useSiteCore } from "../../../hooks/useSiteCore";
import { filterSiteCoreKeys } from "../siteCore/SiteCoreKeys";

interface DeleteMultipleFilterProps {
  showModal: boolean;
  handleModalClose: () => void;
  handlePrimaryButtonClick: () => void;
  listOfFilters: any;
  refetchListOfFilters: () => void;
  callbackAction: () => void;
}

export const DeleteMultipleFilterModal: FC<DeleteMultipleFilterProps> = ({
  showModal,
  handleModalClose,
  handlePrimaryButtonClick,
  listOfFilters,
  refetchListOfFilters,
  callbackAction,
}) => {
  const dispatch = useDispatch();
  const { commonLabels, fetchLabel } = useSiteCore();

  const [selectedFilters, setSelectedFilters] = useState<number[]>([]);

  const {
    mutateAsync: deleteSavedFilter,
    isSuccess,
    isPending,
    isError: isDeleteError,
  } = useDeleteClientSavedFiltersMutation();

  const fetchFilterPopupLabel = (key: string) => {
    return fetchLabel(filterSiteCoreKeys.filterPopupKey, key);
  };

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isPending,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isPending]);

  useEffect(() => {
    if (isSuccess) {
      handlePrimaryButtonClick();
      refetchListOfFilters();
      callbackAction();
    }
    if (isDeleteError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isSuccess, isDeleteError]);

  const handleDeleteConfirmation = () => {
    deleteSavedFilter(selectedFilters);
  };

  const handleSelection = (selectedId: number) => {
    if (selectedFilters.includes(selectedId)) {
      const updatedSelection = selectedFilters.filter(
        (id) => id !== selectedId,
      );
      setSelectedFilters(updatedSelection);
    } else {
      setSelectedFilters([...selectedFilters, selectedId]);
    }
  };

  return (
    <MuiModal
      open={showModal}
      onClose={handleModalClose}
      title={fetchFilterPopupLabel(filterSiteCoreKeys.existingFilters)}
      primaryButtonTitle={commonLabels.delete}
      secondaryButtonTitle={commonLabels.cancel}
      onSecondaryButtonClick={handleModalClose}
      onPrimaryButtonClick={handleDeleteConfirmation}
      disablePrimaryButton={selectedFilters.length < 1}
    >
      <>
        <MuiTypography
          id="exceed-max-filters-info-text"
          variant="h3"
          component="div"
        >
          {fetchFilterPopupLabel(filterSiteCoreKeys.exceededMaxNumberOfFilters)}
        </MuiTypography>

        <DeleteMultipleFiltersModalWrapper>
          <MuiTypography
            id="modal-description"
            variant="h3Bold"
            component={"h3"}
            className="confirm-message"
          >
            {fetchFilterPopupLabel(filterSiteCoreKeys.filters)}
          </MuiTypography>

          <MuiGrid size={{ sm: 12, md: 12, xs: 12 }}>
            <MuiBox>
              <Container className="delete-multiple-filters-list-container">
                <List>
                  {listOfFilters.map((filter: any) => (
                    <ListItem
                      className="filterList"
                      key={filter?.id}
                      id={filter?.id}
                    >
                      <Checkbox
                        checked={selectedFilters.includes(filter.id)}
                        onChange={() => handleSelection(filter.id)}
                      />
                      <ListItemText primary={filter.name} />
                    </ListItem>
                  ))}
                </List>
              </Container>
            </MuiBox>
          </MuiGrid>
        </DeleteMultipleFiltersModalWrapper>
      </>
    </MuiModal>
  );
};
